import * as React from 'react';
import {View} from 'react-native';
import {useSelector} from 'react-redux';
import {get} from 'lodash';

import Text from '/component/base/Text';
import Gutter from '/component/layout/Gutter';
import {getDaysLeftForBonus} from '/component/screen/Dashboard/BonusBannerCards/IncentiveTripBanner/IncentiveTripBanner.util';
import {CoachRank} from '/component/screen/Dashboard/BonusBannerCards/IncentiveTripBanner/Requirements/CoachRank';
import {FrontlineQualifyingVolume} from '/component/screen/Dashboard/BonusBannerCards/IncentiveTripBanner/Requirements/FrontlineQualifyingVolume';
import {NewClients} from '/component/screen/Dashboard/BonusBannerCards/IncentiveTripBanner/Requirements/NewClients';
import {NewCoach} from '/component/screen/Dashboard/BonusBannerCards/IncentiveTripBanner/Requirements/NewCoach';
import {SupportPath} from '/component/screen/Dashboard/BonusBannerCards/IncentiveTripBanner/Requirements/SupportPath';
import {IncentiveTrip, Shared} from '/constant/TestIDs';
import {withCompetency, withTheme} from '/provider/ThemeProvider';
import {useTranslation} from '/provider/TranslationProvider';
import {variablesFeatureFlag} from '/redux/selector/remoteConfig.selector';
import {generateComponentCallback} from '/util/component.util';
import {withHOC} from '/util/hoc.util';
import testProperties from '/util/testing/testProperties';

import {styles} from './EligibleForIncentiveTripReportContent.style';

interface RecordData {
  supportPathCount: {currentCount?: number; targetCount?: number};
  newCoachCount: {currentCount?: number; targetCount?: number};
  fqv: {currentVolume?: string; targetVolume?: string};
  orderingEntities: any;
  rankAchievements: any;
  path: number;
  goalRank: {description?: string};
  currentDate: string;
  incentiveEndDate: string;
}

interface EligibleForIncentiveTripReportContentProps {
  recordData: RecordData;
  getTheme: (key: string) => string;
}

export const EligibleForIncentiveTripReportContent: React.FC<
  EligibleForIncentiveTripReportContentProps
> = ({
  recordData = {
    supportPathCount: {currentCount: 0, targetCount: 0},
    newCoachCount: {currentCount: 0, targetCount: 0},
    fqv: {currentVolume: '0', targetVolume: '0'},
    orderingEntities: {},
    rankAchievements: [],
    path: 0,
    goalRank: {description: ''},
    currentDate: '',
    incentiveEndDate: '',
  },
  getTheme = () => '',
}) => {
  const {tt, ts} = useTranslation('reportCardContent.eligibleForIncentiveTrip');
  const iconColor = getTheme('competency.backgroundColor.backgroundColor');

  const {
    supportPathCount,
    newCoachCount,
    fqv,
    orderingEntities,
    rankAchievements,
    path,
    goalRank,
    currentDate,
    incentiveEndDate,
  } = recordData;

  const daysLeftForBonus = getDaysLeftForBonus(currentDate, incentiveEndDate);
  const remoteFeatureFlag = useSelector(variablesFeatureFlag);
  const showIncentiveUpdate = get(
    remoteFeatureFlag,
    'showIncentiveUpdate',
    false,
  );

  return (
    <View style={styles.base}>
      <View style={styles.header}>
        <Gutter>
          {tt(
            'description',
            generateComponentCallback({
              component: (
                <Text
                  style={styles.headerText}
                  variant="heading"
                  {...testProperties(IncentiveTrip.itTitle)}>
                  Eligible for OPTA
                  <Text variant="small">{'VIA '}</Text>
                  Incentive Trip 2024.
                </Text>
              ),
            }),
          )}
        </Gutter>
      </View>
      <Gutter>
        <View style={{...styles.incentiveTripContent}}>
          <View style={styles.heading}>
            <View style={styles.subHeading}>
              <Text
                variant="heading"
                style={styles.requirements}
                {...testProperties(IncentiveTrip.itRequirementsLabel)}>
                {ts('requirements', 'Requirements')}
              </Text>
              {tt('path', () => (
                <Text
                  variant="button"
                  emphasis="medium"
                  style={styles.path}
                  {...testProperties(IncentiveTrip.itPathLabel)}>
                  Path {path}
                </Text>
              ))}
              {tt('daysLeft', () => (
                <Text
                  style={styles.daysLeft}
                  emphasis="medium"
                  variant="button"
                  {...testProperties(Shared.daysLeft)}>
                  {daysLeftForBonus}{' '}
                  {Number(daysLeftForBonus) === 1 ? 'day' : 'days'} left to
                  qualify
                </Text>
              ))}
            </View>
          </View>
          <CoachRank
            iconColor={iconColor}
            rankAchievements={rankAchievements}
            coachGoalRank={goalRank}
            path={path}
            currentDate={currentDate}
            showIncentiveUpdate={showIncentiveUpdate}
          />
          <NewClients
            iconColor={iconColor}
            orderingEntities={orderingEntities}
          />
          {path === 1 && !showIncentiveUpdate && (
            <FrontlineQualifyingVolume iconColor={iconColor} fqv={fqv} />
          )}
          {path !== 5 && !showIncentiveUpdate && (
            <NewCoach iconColor={iconColor} newCoachCount={newCoachCount} />
          )}
          {(path === 4 || path === 5) && (
            <SupportPath
              iconColor={iconColor}
              supportPathCount={supportPathCount}
            />
          )}
        </View>
      </Gutter>
    </View>
  );
};

export const EligibleForIncentiveTripReportContentConnected = withHOC(
  withCompetency,
  withTheme,
)(EligibleForIncentiveTripReportContent);
